import { type FC, Fragment } from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Button,
  Card,
  CardActions,
  CardHeader,
  Chip,
  Divider,
  List,
  ListItemButton,
  ListItemText,
  Stack,
  Typography,
} from "@mui/material";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";
import { PagePaths } from "@/admin/shared/constants/routes";
import type { OpenSupportRequestRow } from "@/admin/entities/assistant";
import { statusChipColor, statusLabel, typeLabel } from "../model/labels";
import {
  formatRequestAge,
  requestPreview,
  supportRequestHref,
} from "../model/openRequests";

interface OpenSupportRequestsListProps {
  rows: OpenSupportRequestRow[];
  count: number;
}

/**
 * «Открытые обращения» на дашборде админки: последние ждущие ответа команды
 * (новые и с ответом зрителя, как значок меню), строка открывает диалог
 * обращения, внизу ссылка на все.
 */
export const OpenSupportRequestsList: FC<OpenSupportRequestsListProps> = ({
  rows,
  count,
}) => (
  <Card>
    <CardHeader
      avatar={<SupportAgentIcon color="warning" />}
      title="Открытые обращения"
      action={<Chip size="small" color="warning" label={count} />}
    />
    <List disablePadding>
      {rows.map((request, index) => (
        <Fragment key={request.id}>
          {index > 0 && <Divider />}
          <ListItemButton
            component={RouterLink}
            to={supportRequestHref(request.id)}
            sx={{ gap: 2, alignItems: "flex-start" }}
          >
            <ListItemText
              primary={requestPreview(request.message)}
              secondary={
                <Stack
                  component="span"
                  direction="row"
                  spacing={1}
                  sx={{
                    flexWrap: "wrap",
                    rowGap: 0.5,
                    alignItems: "center",
                    mt: 0.5,
                  }}
                >
                  <Chip
                    component="span"
                    size="small"
                    variant="outlined"
                    label={typeLabel[request.type] ?? request.type}
                  />
                  <Typography component="span" variant="caption">
                    {request.platform?.title ??
                      request.organization?.title ??
                      ""}
                  </Typography>
                  {request.user?.email && (
                    <Typography component="span" variant="caption">
                      {request.user.email}
                    </Typography>
                  )}
                  <Typography component="span" variant="caption">
                    {formatRequestAge(request.createdAt)}
                  </Typography>
                </Stack>
              }
              slotProps={{ primary: { variant: "body2", noWrap: true } }}
              sx={{ minWidth: 0, my: 0 }}
            />
            <Chip
              size="small"
              color={statusChipColor[request.status] ?? "default"}
              label={statusLabel[request.status] ?? request.status}
            />
          </ListItemButton>
        </Fragment>
      ))}
    </List>
    <CardActions sx={{ justifyContent: "flex-end" }}>
      <Button
        size="small"
        component={RouterLink}
        to={PagePaths.assistantRequests}
      >
        {`Все обращения (${count})`}
      </Button>
    </CardActions>
  </Card>
);
